
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { client } from "..";
import { GET_ALL_CLIENTS } from "../Queries/Users";
import { setLoader } from "./loaderSlice";
import { EDIT_CLIENT, UPDATE_CLIENT } from "../Queries/Orders";
import dayjs from "dayjs";
import { CREATE_SUBSCRIPTION_INVOICE } from "../Queries/Orders";


// Initial state of your slice
const initialState = {
  clientData: {},
  clients: [],
  loading: false,
  error: null,
};

// Thunk to fetch clients
export const fetchClients = createAsyncThunk(
  "clients/fetchClients",
  async ({ silent = false } = {}, { dispatch, rejectWithValue }) => {
    try {
      if (!silent) dispatch(setLoader(true));
      const { data } = await client.query({
        query: GET_ALL_CLIENTS,
        fetchPolicy: "network-only"
      });
      dispatch(setClients(data.getAllClients.response))
      return data.getAllClients.response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
    finally {
      dispatch(setLoader(false));
    }
  }
);

export const updateClientsAsync = createAsyncThunk(
  "clients/updateClientsAsync",
  async (updatedClient, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_CLIENT,
        variables: { input: updatedClient },
      });
      if (data.updateclient.status === "200") {
        dispatch(updateClient(updatedClient));
        return updatedClient;
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const editClientsAsync = createAsyncThunk(
  "clients/editClientsAsync",
  async (editClient, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: EDIT_CLIENT,
        variables: { input: editClient },
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const clientSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    setClients: (state, action) => {
      state.clients = action.payload;
    },
    addClient: (state, action) => {
      state.clients = [action.payload, ...state.clients];
    },
    updateClient: (state, action) => {
      const updatedClient = action.payload;
      const index = state.clients.findIndex(client => client.id === updatedClient.id);

      if (index !== -1) {
        state.clients[index] = { ...updatedClient };
      }
    },
    setClientProfile: (state, action) => {
      state.clientData = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle loading state
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Handle success state
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
      })
      // Handle error state
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});


export const DEFAULT_COMPANYS_ADDRESS = `"2108 N. St.",
         "Suite 4658",
         "Sacramento, 
         California 95816",
         "United States",
         "+1 510-768-7742"`;

const subscriptionInitialState = {
  invoice: null,
  loading: false,
  error: null,
  form: {
    clientId: null,
    products: [],
    selectedProduct: null,
    price: "",
    qty: 1,
    durationDate: null,
    endDate: null,
    lastSelectedProduct: null,
    companyAddress: DEFAULT_COMPANYS_ADDRESS,
    accountNumber: "",
    bankName: "",
    bankAddress: "",
    newProductForm: {
      name: "",
      price: "",
      qty: 1,
      billingType: "recurring",
      billingPeriod: "",
      tax: null,
      image: null,
      description: "",
    },
  },
};

// Thunk for creating subscription invoice
export const createSubscriptionInvoice = createAsyncThunk(
  "subscriptionInvoice/create",
  async ({ clientId, input, isEdit = false, index = null }, thunkAPI) => {
    try {
      const response = await client.mutate({
        mutation: CREATE_SUBSCRIPTION_INVOICE,
        variables: { clientId, input, isEdit, index },
      });
      return response.data.createSubscriptionInvoice;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Mutation failed");
    }
  }
);

const subscriptionInvoiceSlice = createSlice({
  name: "subscriptionInvoice",
  initialState: subscriptionInitialState,
  reducers: {
    resetInvoiceState: (state) => {
      state.invoice = null;
      state.loading = false;
      state.error = null;
    },
    setFormField: (state, action) => {
      const { field, value } = action.payload;
      state.form[field] = value;
    },
    setFormProducts: (state, action) => {
      state.form.products = (action.payload || []).filter(
        (p, i) => p.productId !== "undefined" || p.productId === ""
      );
    },
    setSelectedProduct: (state, action) => {
      state.form.selectedProduct = action.payload ?? null;
    },
    setLastSelectedProduct: (state, action) => {
      state.form.lastSelectedProduct = action.payload;
    },
    setNewProductForm: (state, action) => {
      state.form.newProductForm = action.payload;
    },
    resetForm: (state) => {
      state.form = {
        products: [],
        selectedProduct: null,
        price: "",
        qty: null,
        durationDate: null,
        endDate: null,
        billStarting: null,
        lastSelectedProduct: null,
        companyAddress: DEFAULT_COMPANYS_ADDRESS,
        accountNumber: "",
        bankName: "",
        bankAddress: "",
        newProductForm: {
          name: "",
          price: "",
          qty: null,
          billingType: "recurring",
          billingPeriod: "",
          tax: null,
          image: null,
          description: "",
        },
      };
      localStorage.removeItem("subscriptionForm");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSubscriptionInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSubscriptionInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoice = action.payload;
      })
      .addCase(createSubscriptionInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setClients,
  addClient,
  updateClient,
  setClientProfile,
} = clientSlice.actions;

export const {
  resetInvoiceState,
  setFormField,
  setFormProducts,
  setSelectedProduct,
  setLastSelectedProduct,
  setNewProductForm,
  resetForm,
} = subscriptionInvoiceSlice.actions;

export default clientSlice.reducer;
export const subscriptionInvoiceReducer = subscriptionInvoiceSlice.reducer;


